import * as React from "react";
import { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Collection, CollectionSite, TrapType } from "../../../types";
import { parseTimeToLocal } from "../../../utils/timeHelper";
import { Button, LinearProgress } from "@mui/material";
import EnhancedTableHead from "./TableHead";

const ROW_HEIGHT = 53;

const collectionMethodGetter = (
  value: string,
  trapTypeOptions: CollectionSite[]
) => {
  let res: string = "";
  trapTypeOptions.forEach((cur: TrapType) => {
    if (cur.id === value) {
      res = cur.displayName || "";
    }
  });
  return res;
};

const collectionSiteNameGetter = (
  value: string,
  trapSiteOptions: TrapType[]
) => {
  let res: string = "";
  trapSiteOptions.forEach((cur) => {
    if (cur.id === value) {
      res = cur.displayName || "";
    }
  });
  return res;
};

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]?: number | string | any },
  b: { [key in Key]?: number | string | any }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

interface Props {
  collections: Collection[];
  collectionCount: number;
  paginationModel: { page: number; pageSize: number };
  setPaginationModel: (id: { page: number; pageSize: number }) => void;
  handleSelectCollection: (id: string) => void;
  trapSiteOptions: CollectionSite[];
  trapTypeOptions: TrapType[];
  loading: boolean;
  setSortModel: (order: "desc" | "asc", sortBy: keyof Collection) => void;
}

const EnhancedTable: React.FC<Props> = ({
  collections,
  collectionCount,
  paginationModel,
  setPaginationModel,
  handleSelectCollection,
  trapSiteOptions,
  trapTypeOptions,
  loading,
  setSortModel,
}) => {
  const [order, setOrder] = React.useState<Order>("desc");
  const [orderBy, setOrderBy] = React.useState<keyof Collection>("createdAt");
  const [autoPerPage, setAutoPerPage] = React.useState(0);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Collection
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setSortModel(isAsc ? "desc" : "asc", property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPaginationModel({ page: newPage, pageSize: paginationModel.pageSize });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPaginationModel({ page: 0, pageSize: parseInt(event.target.value, 10) });
  };

  const visibleRows = React.useMemo(
    () =>
      [...collections]
        .sort(getComparator(order, orderBy))
        .slice(
          paginationModel.page * paginationModel.pageSize,
          paginationModel.page * paginationModel.pageSize +
            paginationModel.pageSize
        ),
    [order, orderBy, paginationModel, collections]
  );

  const handleEditClick = (id: any) => () => {
    handleSelectCollection(id as string);
  };

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      const containerHeight = containerRef.current.clientHeight;
      const calculatedRows = Math.floor(containerHeight / ROW_HEIGHT);
      setAutoPerPage(calculatedRows > 0 ? calculatedRows : 1);
      setPaginationModel({
        page: 0,
        pageSize: calculatedRows,
      });
    }
    // eslint-disable-next-line
  }, [containerRef]);

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2, height: "100%" }}>
        <TableContainer
          sx={{
            height: "100%",
          }}
          ref={containerRef}
        >
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size="small"
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={collectionCount}
            />

            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={99} sx={{ p: 0 }}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {visibleRows &&
                visibleRows.map((row, index) => {
                  const labelId = `enhanced-table-checkbox-${index}`;
                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={row.id}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        padding="none"
                        align={"center"}
                      >
                        {parseTimeToLocal(row.createdAt)}
                      </TableCell>
                      <TableCell align="center">{row.displayName}</TableCell>
                      <TableCell align="center">
                        {collectionSiteNameGetter(
                          row.collectionSiteId || "",
                          trapSiteOptions
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {collectionMethodGetter(
                          row.trapTypeId || "",
                          trapTypeOptions
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {parseTimeToLocal(row.collectionDate)}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          onClick={handleEditClick(row.id)}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, autoPerPage]}
          component="div"
          count={collectionCount}
          rowsPerPage={paginationModel.pageSize}
          page={paginationModel.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default EnhancedTable;
